<template>
    <fieldset aria-describedby="content-info-text">
        <legend
            v-if="listHeading"
            :id="uniqueId"
            class="legendelement"
        >
            {{ $t(listHeading) }}
        </legend>
        <div class="radiobuttonelement">
            <div
                :key="forceUpdateKey"
                role="radiogroup"
            >
                <div
                    v-for="(item, index) in visibleRadioButtonList"
                    :key="index"
                    class="rb-ddl-item"
                >
                    <div class="radio-button-group">
                        <input
                            :id="item.radioValue + index"
                            type="radio"
                            :value="index"
                            :checked="item.labelText === selectedRadioButton"
                            :name="item.radioValue + index"
                            @change="changeRadioButton(item, $event)"
                            @keydown.enter="changeRadioButton(item, $event)"
                        >
                        <label :for="item.radioValue + index">{{ $t(item.labelText) }}</label>
                    </div>
                    <div
                        v-show="item.dropDownListItems && (itemSelected(item) || showAllDropDownsDisabled)"
                        class="dropdown"
                    >
                        <label
                            class="visually-hidden"
                            :for="item.radioValue + '-select-' + index"
                        >{{ $t(item.labelText) }}</label>
                        <select
                            :id="item.radioValue + '-select-' + index"
                            :disabled="showAllDropDownsDisabled && !itemSelected(item)"
                            @change="changeDropDown(($event.target as HTMLSelectElement)?.selectedIndex, item, $event)"
                        >
                            <option
                                v-for="(option, i) in item.dropDownListItems"
                                :key="i"
                                :value="option.identifier"
                                :selected="setDefaultDropdownValue(item, option)"
                            >
                                {{ $t(option.labelText) }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <button
                v-if="displayShowMoreElement"
                :class="['toggle-item-visibility', 'element-with-path-icon', allItemsVisible ? 'open' : '']"
                @click="showOrHideElements()"
            >
                {{ showHideItemsLabel }}
            </button>
        </div>
    </fieldset>
</template>

<script lang="ts">
    import { Vue, Component, Prop, Emit, Watch } from "vue-facing-decorator"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import PageWhitelistUtils from "@/helpers/pageWhitelistUtils"

    @Component
    export default class RadioButtonListWithDropdown extends Vue {
        allItemsVisible: boolean = false
        selectedRadioButton: string = this.defaultRadio ?? ""
        defaultButton: string | undefined = ""
        displayShowMoreElement: boolean = false
        // Suodatettu lista radio buttoneista. Sama kuin radioButtonList property mutta tästä on suodatettu whitelistalta puuttuvat sisältösivut.
        filteredRadioButtonList: Array<RadioButtonModel> = []
        // Lista UI:lla näytettävistä radio buttoneista. Sama kuin filteredRadioButtonList mutta mikäli osa radiobuttoneista on piilotettu showmore linkin alle niin ne on poistettu myös tästä listasta.
        visibleRadioButtonList: Array<RadioButtonModel> = []
        firstLoad: boolean = true
        uniqueId: string = ""
        ticks: number = 0
        forceUpdateKey = 0

        @Prop({ type: Array, default: [] }) radioButtonList!: Array<RadioButtonModel>
        @Prop() visibleItems: number | undefined
        @Prop() listHeading: string | undefined
        @Prop() defaultRadioButton: string | undefined
        @Prop({ type: Boolean, default: false }) showAllDropDownsDisabled!: boolean
        @Prop({ type: Boolean, default: true }) whitelistSubjects!: boolean // Suodatetaanko aihevalinnat whitelistan mukaan
        @Prop({ type: String }) subTopic!: string
        @Prop({ type: Boolean, default: false }) selectByUrl!: boolean

        // Näkymä jossa vain radiobuttonit listattuna. Oli ongelma että event laukesi kun syvemmällä sisällä oleva arvo muuttui. #29102
        get RadioButtonListTopView() {
            return this.radioButtonList == null ? null : this.radioButtonList.map(i => ({ label: i.labelText, dropDownListItems: i.dropDownListItems }))
        }

        created() {
            this.uniqueId = "id-" + Math.random().toString(16).slice(2)
        }

        mounted(): void {
            // Suodatetaan RadioButtonList whitelistan mukaan
            this.filterRadioButtonList()
            if (this.filteredRadioButtonList.length === this.radioButtonList.length || !this.whitelistSubjects) {
                if (!this.subTopic && !this.selectByUrl && this.radioButtonList.length > 0
                    && this.defaultButton !== this.radioButtonList[0].labelText
                    && this.defaultButton !== "App.RadioList.SearchLocations") {
                    // DefaultRadioButton on eri kuin radioButtonListan ensimmäinen radio button. Laukaistaan radio buttonin valinta, jotta ladataan oikea data.
                    this.emitRadioButtonSelectionChanged()
                }
            }
        }

        async activated(): Promise<void> {
            this.forceUpdateKey += 1 // Pakotetaan radiobuttoneiden päivitys, jotta oikea radiobutton näkyy varmasti valittuna
        }

        @Watch("RadioButtonListTopView", { deep: true })
        onRadioButtonListTopView() {
            // RadioButtonList property on päivitetty komponentin ulkopuolelta. Suoritetaan listan suodatus.
            this.filterRadioButtonList()
        }

        // Esitetään ruudulla "Näytä enemmän aiheita" valinta vain, jos
        //      - Asetettu raja näytettäville aiheille (this.visibleItems != null)
        //      - Ja rajoitus on pienempi kuin suodatetun listan koko (this.visibleItems < this.filteredRadioButtonList.length)
        updateDisplayShowMoreElement() {
            this.displayShowMoreElement = this.visibleItems != null && this.visibleItems < this.filteredRadioButtonList.length
        }

        filterRadioButtonList() {
            if (this.whitelistSubjects) {
                let modelFound = false

                // Suodatetaan pois niiden aiheiden radiobuttonit, joita ei ole listattu whitelistalla.
                const pageWhitelistUtils = new PageWhitelistUtils()
                this.filteredRadioButtonList = []
                for (const model of this.radioButtonList) {
                    const subjectName = model.groupName !== undefined && model.groupName !== "" ? model.groupName : model.radioValue
                    if (pageWhitelistUtils.isSubjectOnWhiteList(subjectName)) {
                        this.filteredRadioButtonList.push(model)
                    }
                }

                if (this.subTopic && this.selectByUrl && this.filteredRadioButtonList.length > 0) {
                    for (const model of this.filteredRadioButtonList) {
                        if (model.radioValue === this.subTopic) {
                            this.defaultButton = model.labelText
                            this.selectedRadioButton = model.labelText
                            modelFound = true
                        }
                    }
                }
                else if (modelFound === false && this.filteredRadioButtonList.length > 0) {
                    this.defaultButton = this.filteredRadioButtonList[0].labelText
                    // mikäli aihevalintaan ei tulla url-parametrilla, asetetaan listan ensimmäinen radio button valituksi
                    this.selectedRadioButton = this.filteredRadioButtonList[0].labelText
                    // Jos suodatetun listan ensimmäisellä radiobuttonilla on dropdown niin dropdownin ensimmäinen item pitää asettaa valituksi,
                    // jotta UI:lla näytetään oikea data.
                    if (this.filteredRadioButtonList[0].dropDownListItems && this.filteredRadioButtonList[0].dropDownListItems.length > 0) {
                        this.changeDropDown(0, this.filteredRadioButtonList[0], null)
                    }
                }

                // jos filtteröity lista on eripituinen kuin alkuperäinen (ts. jotkin aiheet on piilotettu),
                // tarkistetaan sisältyykö default-arvo (joko propin kautta tai url-parametrina tullut) filtteröityyn listaan.
                // Jos se löytyy, asetetaan sekä radio buttonin että mahdollisen dropdownin arvot tämän mukaan
                if (this.filteredRadioButtonList.length !== this.radioButtonList.length) {
                    if (this.filteredRadioButtonList.length > 0) {
                        for (const model of this.filteredRadioButtonList) {
                            if (this.defaultButton === model.labelText) {
                                // Asetetaan suodatetun listan default-arvo radiobuttoneista valituksi
                                this.selectedRadioButton = model.labelText
                                // Jos suodatetun listan default-radiobuttonilla on dropdown niin dropdownin ensimmäinen item pitää asettaa valituksi,
                                // jotta UI:lla näytetään oikea data.
                                if (model.dropDownListItems && model.dropDownListItems.length > 0) {
                                    this.changeDropDown(0, model, null)
                                }
                                break
                            }
                        }
                    }
                    else {
                        this.selectedRadioButton = ""
                    }
                    this.emitRadioButtonSelectionChanged()

                    // Emitoidaan päivitetty radiobutton lista
                    this.radioButtonListChanged()
                }

                if (this.subTopic && this.selectByUrl && modelFound === true) {
                    this.emitByUrl()
                }
            }
            else {
                this.filteredRadioButtonList = this.radioButtonList
            }
            // Päivitetään näkyvissä olevat radiobuttonit
            this.updateVisibleRadioButtons()
        }

        updateVisibleRadioButtons() {
            // Päivitetään ShowMore elementin näkyvyys
            this.updateDisplayShowMoreElement()

            if (this.visibleItems && !this.allItemsVisible) {
                // Poistetaan ShowMore elementin alle jäävät radio buttonit näkyvistä
                this.visibleRadioButtonList = this.filteredRadioButtonList.slice(0, this.visibleItems)

                // Suoritetaan ensilatauksen logiikka vasta kun lista sisältää dataa. Muuten tyhjällä alustuksella
                // (esim. kun radioButtonList alustetaan ensin tyhjänä) showOrHideElements() laukeaisi virheellisesti
                // ja allItemsVisible/firstLoad menisivät väärään tilaan ennen oikean datan saapumista.
                if (this.firstLoad === true && this.filteredRadioButtonList.length > 0) {
                    // Tarkistetaan, onko valittu radiobutton näkyvillä
                    let labelFound = false
                    for (const element of this.visibleRadioButtonList) {
                        if (element.labelText === this.selectedRadioButton) {
                            labelFound = true
                        }
                    }
                    if (!labelFound) {
                        this.showOrHideElements()
                        this.firstLoad = false
                    }
                }
            }
            else {
                this.visibleRadioButtonList = this.filteredRadioButtonList
            }
        }

        @Emit("radioButtonsChanged")
        radioButtonListChanged() {
            return this.filteredRadioButtonList
        }

        get defaultRadio(): string | undefined {
            return this.defaultRadioButton
        }

        itemSelected(item: RadioButtonModel): boolean {
            return this.selectedRadio === item.labelText
        }

        initRadioValue(item: RadioButtonModel, index: number): boolean {
            if ((this.subTopic === null || this.subTopic === undefined) && !this.selectByUrl) {
                return (index === 0)
            }
            else if (this.defaultButton !== undefined) {
                return (this.defaultButton === item.labelText)
            }
            else {
                return (this.selectedRadioButton === item.labelText)
            }
        }

        get showHideItemsLabel(): string | undefined {
            if (this.allItemsVisible) {
                return this.$t("App.RadioButtonList.ShowLessItems")
            }
            else {
                return this.$t("App.RadioButtonList.ShowMoreItems")
            }
        }

        showOrHideElements() {
            this.allItemsVisible = !this.allItemsVisible
            // Päivitetään näkyvissä olevat radiobuttonit
            this.updateVisibleRadioButtons()
        }

        // Valittu radiobutton
        get selectedRadio(): string {
            return this.selectedRadioButton
        }

        emitRadioButtonSelectionChanged(): void {
            if (this.selectedRadioButton !== "" && this.selectedRadioButton !== "App.RadioList.SearchLocations") {
                // Laukaistaan radio buttonin valinta. Tämä emitoidaan osiosivuille, jotka sitten hoitavat datan lataamisen.
                for (const model of this.filteredRadioButtonList) {
                    if (model.labelText === this.selectedRadioButton) {
                        this.changeRadioButton(model, null)
                        break
                    }
                }
            }
        }

        emitByUrl(): void {
            for (const model of this.filteredRadioButtonList) {
                if (model.radioValue === this.subTopic) {
                    this.changeRadioButton(model, null)
                    break
                }
            }
        }

        changeRadioButton(item: RadioButtonModel, event): void {
            if (this.selectByUrl === true && item.radioValue) {
                const url = new URL(window.location as any)
                url.searchParams.set("subTopic", item.radioValue)
                window.history.pushState({}, "", url)
                this.$store.dispatch("setSubTopic", item.radioValue)
            }

            this.selectedRadioButton = item.labelText
            this.$emit("selectedRadioButton", item)
            this.updateSelectedDropDownResourceKey(item)
            this.setFocusToElement(2, event)
        }

        changeDropDown(selectedIndex: number, item: RadioButtonModel, event): void {
            let value = ""

            if (item.dropDownListItems) {
                value = item.dropDownListItems[selectedIndex].identifier
            }

            item.selectedDropDownListValue = value
            this.$emit("selectedDropDown", value)
            this.$store.dispatch("setDropDownSelected", value)
            this.updateSelectedDropDownResourceKey(item)
            this.setFocusToElement(2, event)
        }

        // Päivitetään valitun dropdownin kielitermi storeen, jotta se voidaan näyttää RightSlotissa
        updateSelectedDropDownResourceKey(item: RadioButtonModel): void {
            let resourceKey = ""

            // tarkistetaan onko RadioButtonModel tyyppisellä muuttujalla alasvetovalikkoa
            if (item.dropDownListItems !== undefined && item.dropDownListItems !== null && item.dropDownListItems?.length > 0) {
                // Dropdownista ollaan valittu item. Haetaan item.dropDownListItems listalta oikea item
                // ja asetetaan sen kielikoodi storeen.
                if (item.selectedDropDownListValue !== undefined && item.selectedDropDownListValue !== "") {
                    for (const listItem of item.dropDownListItems) {
                        if (listItem.identifier === item.selectedDropDownListValue) {
                            resourceKey = listItem.labelText
                            break
                        }
                    }
                }
                // Jos dropdownista ei olla valittu itemiä niin valinnaksi asetetaan listan ensimmäiseen elementti
                else {
                    // Valitaan ensimmäinen item.dropDownListItems listalta
                    item.selectedDropDownListValue = item.dropDownListItems[0].identifier
                    resourceKey = item.dropDownListItems[0].labelText
                    this.$store.dispatch("setDropDownSelected", item.dropDownListItems[0].identifier)
                }
            }
            else if (this.$store.getters.currentPage === "bittimittari") {
                if (this.$store.getters.selectedDropDownResourceKey !== undefined
                    && (this.listHeading === "App.RadioButtonListHeader.Bittimittari.OperatorsList"
                        || this.listHeading === "App.RadioButtonListHeader.Bittimittari.DataTransferDirection"
                        || item.radioValue === "BittimittariOperaattorikohtaisetTiedot")) {
                    // Bittimittari / Tulokset operaattorin mukaan
                    // Operaattorin nimi asetetaan rightslotin otsikkoon leftslotin koodissa (Bittimittari.vue).
                    // Sitä ei saa ylikirjoittaa tässä ellei aihetta tai sivua vaihdeta.
                    return
                }
            }

            this.$store.dispatch("setSelectedDropDownResourceKey", resourceKey)
        }

        // sets focus to the selected radio button
        setFocusToElement(ticksToWait, event) {
            if (this.ticks < ticksToWait) {
                this.ticks++

                this.$nextTick(() => {
                    this.setFocusToElement(ticksToWait, event)
                })
            }
            else {
                this.ticks = 0

                if (event) {
                    event.target.focus()
                }
            }
        }

        // asetetaan alasvetovalikon oletusvalinta
        setDefaultDropdownValue(item, option) {
            return item.selectedDropDownListValue === option.identifier
        }
    }
</script>

<style scoped>

    .legendelement {
        font-size: 1.125rem;
        font-family: Montserrat;
    }
    .radiobuttonelement {
        margin-bottom: 0.5rem;
    }

    .rb-ddl-item {
        margin-bottom: 7px;
    }

    div.radio-button-group label {
        vertical-align: super;
        line-height: 22px;
    }

    .dropdownelement {
        margin: 0 0 12px 0;
    }

        .dropdownelement select.error {
            background-color: rgba(158,0,59,0.05);
        }

    .dropdown {
        margin-top: 0.9rem;
        margin-bottom: 1.2rem;
    }

        .dropdown > select {
            width: 100%;
        }

    div > div.dropdown::after {
        position: absolute;
        right: 0.625rem;
        top: 0.75rem;
        filter: invert(10%) sepia(27%) saturate(5527%) hue-rotate(205deg) brightness(96%) contrast(106%);
    }

    button.toggle-item-visibility {
        background: none;
        border: none;
        padding: 0;
        display: flex;
        font-family: Open Sans;
        font-size: 1rem;
        color: var(--Traficom-main-color);
        cursor: pointer;
    }

    .element-with-path-icon::after {
        filter: invert(10%) sepia(27%) saturate(5527%) hue-rotate(205deg) brightness(96%) contrast(106%);
        margin-left: 15px;
    }

    .element-with-path-icon.open::after {
        transform: rotateZ(180deg);
    }

    select {
        background-color: #FFFFFF;
    }
</style>
